// src/pages/healthprefs.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "@/components/AuthLayout";
import Input from "@/components/Input";
import ProgressBar from "@/components/ProgressBar";
import { getSupabase, currentUser } from "@/lib/supabase";
import { markCompleted, getStepIndexById, steps } from "@/lib/onboarding";

const DIET_OPTIONS = [
  "Mediterranean",
  "Ketogenic (Keto)",
  "Paleo",
  "Pescatarian",
  "Vegetarian",
  "Vegan",
  "Intermittent Fasting",
];

export default function HealthPrefs() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    diet: "",
    hasAllergies: null as boolean | null,
    allergies: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleNext = async () => {
    setErr(null);
    setSaving(true);
    try {
      const user = await currentUser();
      if (!user) throw new Error("No user found — please log in again.");

      const supabase = getSupabase();
      if (!supabase) throw new Error("Supabase client not initialized.");

      const updateData = {
        diet_style: form.diet || null,
        food_intolerances:
          form.hasAllergies && form.allergies.trim().length > 0
            ? form.allergies
            : null,
      };

      const { error } = await supabase
        .from("clients")
        .update(updateData)
        .eq("user_id", user.id);

      if (error) throw error;

      markCompleted("healthPrefs");
      const nextIdx = getStepIndexById("healthPrefs") + 1;
      const nextPath = steps[nextIdx]?.path || "/device-integrations";
      nav(nextPath);
    } catch (e: any) {
      setErr(e.message ?? "Failed to save health preferences.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthLayout>
      <div className="absolute top-4 left-4 z-10">
  <button
    onClick={() => navigate(-1)}
    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#0f172a] transition-colors"
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
    Back
  </button>
</div>
      
      <div className="space-y-6 text-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0f172a]">Health & Preferences</h1>
          <p className="text-sm sm:text-base text-gray-500 mt-2">
            Tell us about your diet and any food intolerances.
          </p>
        </div>

        {/* Diet Type – Full-width pills */}
        <div>
          <label className="block text-left text-sm font-medium text-gray-600 mb-2">
            Diet type
          </label>
          <div className="space-y-3">
            {DIET_OPTIONS.map((option) => (
              <button
                key={option}
                onClick={() => setForm((f) => ({ ...f, diet: option }))}
                className={`
                  w-full h-12 rounded-full text-sm sm:text-base font-semibold transition-all duration-200
                  flex items-center justify-center
                  ${form.diet === option
                    ? "bg-[#a78bfa] text-[#0f172a] shadow-lg shadow-[#a78bfa]/30"
                    : "bg-gray-800 text-[#0f172a] border border-gray-700 hover:bg-gray-700"
                  }
                  active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#a78bfa]/50
                `}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* Allergies – Yes/No toggle */}
        <div>
          <label className="block text-left text-sm font-medium text-gray-600 mb-2">
            Do you have any allergies?
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setForm((f) => ({ ...f, hasAllergies: true }))}
              className={`
                h-12 rounded-full font-semibold transition-all duration-200
                ${form.hasAllergies === true
                  ? "bg-gray-700 text-[#0f172a] border border-gray-600"
                  : "bg-gray-800 text-gray-500 border border-gray-700"
                }
                active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#a78bfa]/50
              `}
            >
              Yes
            </button>
            <button
              onClick={() =>
                setForm((f) => ({ ...f, hasAllergies: false, allergies: "" }))
              }
              className={`
                h-12 rounded-full font-semibold transition-all duration-200
                ${form.hasAllergies === false
                  ? "bg-gray-700 text-[#0f172a] border border-gray-600"
                  : "bg-gray-800 text-gray-500 border border-gray-700"
                }
                active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#a78bfa]/50
              `}
            >
              No
            </button>
          </div>
        </div>

        {/* Conditional Allergies Input */}
        {form.hasAllergies === true && (
          <div>
            <label className="block text-left text-sm font-medium text-gray-600 mb-1">
              Description
            </label>
            <Input
              type="text"
              placeholder="Enter your allergies or intolerances..."
              value={form.allergies}
              onChange={(e) =>
                setForm((f) => ({ ...f, allergies: e.target.value }))
              }
              className="w-full bg-gray-800 border-gray-700 text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:ring-[#a78bfa]"
            />
          </div>
        )}

        {/* Next Button */}
        <button
          onClick={handleNext}
          disabled={saving || !form.diet}
          className={`
            w-full bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] font-semibold
            rounded-2xl py-3 transition-all duration-200
            active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed
            ${!form.diet || saving ? "opacity-60" : ""}
          `}
        >
          {saving ? "Saving..." : "Next"}
        </button>

        {err && <p className="text-sm text-red-400">{err}</p>}

        <div className="my-6 border-t border-gray-800" />

        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <span className="w-2 h-2 bg-[#a78bfa] rounded-full" />
          Input
        </div>

        <footer className="text-center text-xs text-gray-500">
          © 2025 Input. All rights reserved.
        </footer>

        <div className="mt-6">
          <ProgressBar step={7} total={10} />
        </div>
      </div>
    </AuthLayout>
  );
}
